// components/VirtualizedConversaList.tsx
import { JSX, useCallback, useRef } from 'react';
import { FixedSizeList as List, ListOnItemsRenderedProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

interface Props {
  conversas: any[];
  itemRenderer: (conversa: any) => JSX.Element;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  itemHeight?: number;
}

const VirtualizedConversaList: React.FC<Props> = ({
  conversas,
  itemRenderer,
  isLoadingMore,
  hasMore,
  loadMore,
  itemHeight = 128
}) => {
  const loadMoreRef = useRef(false);
  const listRef = useRef<List>(null);

  // Use onItemsRendered to detect when user scrolls near the end
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      // Trigger when we're within 3 items of the end
      if (
        conversas.length > 0 &&
        visibleStopIndex >= conversas.length - 3 &&
        hasMore &&
        !isLoadingMore &&
        !loadMoreRef.current
      ) {
        loadMoreRef.current = true;
        console.log('[VirtualizedList] Loading more... visibleStopIndex:', visibleStopIndex, 'total:', conversas.length);
        loadMore();
        // Reset lock after delay to prevent rapid duplicate calls
        setTimeout(() => { loadMoreRef.current = false; }, 1500);
      }
    },
    [conversas.length, hasMore, isLoadingMore, loadMore]
  );

  const itemCount = hasMore ? conversas.length + 1 : conversas.length;

  const renderRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    if (index >= conversas.length) {
      return (
        <div style={style} className="flex justify-center items-center py-4 text-sm text-gray-500">
          {isLoadingMore ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              <span>Carregando mais...</span>
            </div>
          ) : (
            <button
              onClick={() => loadMore()}
              className="text-blue-500 hover:text-blue-600 cursor-pointer text-sm font-medium"
            >
              Carregar mais conversas
            </button>
          )}
        </div>
      );
    }

    return (
      <div style={style}>
        {itemRenderer(conversas[index])}
      </div>
    );
  };

  return (
    <div className="h-full w-full">
      <AutoSizer className="overflow-hidden">
        {({ height, width }: { height: number; width: number }) => (
          <List
            ref={listRef}
            height={height}
            width={width}
            itemCount={itemCount}
            itemSize={itemHeight}
            onItemsRendered={handleItemsRendered}
            overscanCount={5}
          >
            {({ index, style }) => renderRow({ index, style })}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedConversaList;
